import React, { useEffect, useState } from 'react';
import { IContractor, IRoute, IUser } from '../../models';
import PaneHeadline from '../shared/paneHeadline';
import { faBusinessTime, faFilter } from '@fortawesome/free-solid-svg-icons';
import OrderOverviewGrid from './OrderOverviewGrid';
import { locale } from '../../common/localization/localizationService';
import { Spinner2 } from '../shared/spinner2';
import './orderOverview.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IFraction } from '../../models/Fraction.model';
import NewOrderDialog from '../dashboard/newOrderModal';
import ContractorsService from '../../services/ContractorsService';
import { getOrderStatus, hasRole } from '../../utils/GeneralUtils';
import EventBus from '../../common/EventBus';
import OrderCalendar from '../orderCalendar';
import { filterByDropdowns } from '../orderCalendar/filters';
import OrderOverviewFilter from './OrderOverviewFilter';
import moment from 'moment';
import { UserRoles } from '../../redux/modules/auth';
import { AsyncAction } from '../../utils';

export interface IOrderOverviewProps {
  orderActions: any;
  impersonatedUser: any;
  authentication: IUser;
  orderOverview: Array<any>;
  isLoading: boolean;
  contractors: any;
  getContractors: any;
  allFractions: Array<IFraction>;
  orderOverviewFilter: IOrderOverviewFilter;
  statuses: Array<any>;
  routes: Array<IRoute>;
  loadRoutes: any;
  isCalendarVisible: boolean;
  orderCalendarFilter: IOrderOverviewFilter;
  loadCustomerGroups: (ignoreCache?: boolean) => AsyncAction;
}

export interface IOrderUpdateDialogState {
  show: boolean;
  order: any;
}

export interface INewOrderDialogState {
  show: boolean;
}

export interface IOrderOverviewFilter {
  fromDate: Date;
  toDate: Date;
  fractionIds: Array<number>;
  statusIds: Array<number>;
  contractorIds: Array<number>;
  routeIds: Array<string>;
}

export interface FilterOption {
  value: number | string;
  label: string;
}

export default function OrderOverview(props: IOrderOverviewProps): JSX.Element {
  const editDialog: IOrderUpdateDialogState = {
    show: false,
    order: null,
  };

  const [orderOverview, setOveroverview] = useState<Array<any>>(
    props.orderOverview
  );

  const [isFilterVisible, setFilterVisibility] = useState<boolean>(true);

  const [updateDialog, setUpdateDialog] =
    useState<IOrderUpdateDialogState>(editDialog);

  const [isNewOrderDialogVisible, setNewOrderDialogVisibility] =
    useState<boolean>(false);

  const [fractionOptions, setFractionOptions] = useState<Array<FilterOption>>(
    []
  );

  const [contractors, setContractors] = useState<Array<FilterOption>>([]);

  const [isContractorLoading, setIsContractorLoading] =
    useState<boolean>(false);

  const [statuses, setStatuses] = useState<Array<FilterOption>>([]);

  const [routes, setRoutes] = useState<Array<FilterOption>>([]);

  useEffect(() => {
    const filterObject = props.isCalendarVisible
      ? props.orderCalendarFilter
      : props.orderOverviewFilter;
    const formattedFromDate = filterObject.fromDate
      .toISOString()
      .split('T')[0]
      .replace(/-/g, '');
    const formattedToDate = filterObject.toDate
      .toISOString()
      .split('T')[0]
      .replace(/-/g, '');
    if (moment(formattedFromDate).unix() < moment(formattedToDate).unix()) {
      props.orderActions.getOverView(formattedFromDate, formattedToDate);
      props.orderActions.getAllFractions();
      props.getContractors(props.authentication.customerId);
      props.orderActions.getOrderStatuses();
      props.loadRoutes();
      loadContractors();
    }
  }, [props.orderActions, props.getContractors]);

  useEffect(() => {
    if (props.orderOverview) {
      const filterObject = props.isCalendarVisible
        ? props.orderCalendarFilter
        : props.orderOverviewFilter;
      const filteredOrders = filterByDropdowns(
        props.orderOverview,
        filterObject
      );
      setOveroverview(filteredOrders);
    }
  }, [props.orderOverview]);

  useEffect(() => {
    setStatuses(getStatuses());
  }, [props.statuses]);

  useEffect(() => {
    EventBus.emit('fullScreenChange', true);
    props.loadCustomerGroups();

    return () => {
      EventBus.emit('fullScreenChange', false);
    };
  }, []);

  // Frontend side filtering on status, fraction and contractor change
  useEffect(() => {
    const filterObject = props.isCalendarVisible
      ? props.orderCalendarFilter
      : props.orderOverviewFilter;
    const filteredOrders = filterByDropdowns(props.orderOverview, filterObject);
    setOveroverview(filteredOrders);
  }, [
    props.orderOverviewFilter.contractorIds,
    props.orderOverviewFilter.fractionIds,
    props.orderOverviewFilter.statusIds,
    props.orderOverviewFilter.routeIds,
    props.orderCalendarFilter.contractorIds,
    props.orderCalendarFilter.fractionIds,
    props.orderCalendarFilter.statusIds,
    props.orderCalendarFilter.routeIds,
  ]);

  useEffect(() => {
    const options = props.allFractions.map((fraction) => ({
      value: fraction.fractionId,
      label: fraction.name,
    }));
    setFractionOptions(options);
  }, [props.allFractions]);

  useEffect(() => {
    const options = props.routes.map((route) => ({
      value: route.name,
      label: route.name,
    }));
    setRoutes(options);
  }, [props.routes]);

  const loadContractors = () => {
    setIsContractorLoading(true);
    ContractorsService.getSelfContractors().then((resp: Array<IContractor>) => {
      const contractors = resp.map((contractor) => ({
        value: contractor.contractorId,
        label: contractor.contractorName,
      }));
      setContractors(contractors);
      setIsContractorLoading(false);
    });
  };

  const showHideFilters = (): void => {
    setFilterVisibility(!isFilterVisible);
  };

  const editOrder = (order: any) => {
    props.orderActions.getAllFractions();
    const dialogProps = {
      order: order,
      show: true,
    };
    setUpdateDialog(dialogProps);
  };

  const openNewOrderModal = () => {
    setNewOrderDialogVisibility(true);
  };

  const hideNewOrderModal = (rerender: boolean) => {
    setNewOrderDialogVisibility(false);
    const dialogProps = {
      order: {},
      show: false,
    };
    setUpdateDialog(dialogProps);

    if (rerender) {
      const filterObject = props.isCalendarVisible
        ? props.orderCalendarFilter
        : props.orderOverviewFilter;

      const formattedFromDate = filterObject.fromDate
        .toISOString()
        .split('T')[0]
        .replace(/-/g, '');
      const formattedToDate = filterObject.toDate
        .toISOString()
        .split('T')[0]
        .replace(/-/g, '');
      props.orderActions.getOverView(formattedFromDate, formattedToDate, true);
    }
  };

  const getStatuses = () => {
    const statuses: Array<any> = [];
    props.statuses.forEach((status) => {
      status.status = getOrderStatus(status.orderStatusId);
      statuses.push(status);
    });
    return statuses.map((status) => ({
      value: status.orderStatusId,
      label: status.status,
    }));
  };

  return (
    <>
      <div className="order-overview">
        <div className="order-overview-header-row">
          <PaneHeadline
            titleText={locale.general._orderOverview}
            titleIcon={faBusinessTime}
          />
          <div className="order-overview-action-wrapper">
            <button
              className={`show-filters-button ${
                isFilterVisible ? 'filter-active' : ''
              }`}
              onClick={() => showHideFilters()}
            >
              <FontAwesomeIcon icon={faFilter} />
            </button>
            {!props.isCalendarVisible && (
              <button
                className="btn btn-outline-dark mr-2 open-modal-button"
                onClick={() => props.orderActions.setOverviewView(true)}
              >
                {locale.calendar.calendarView}
              </button>
            )}
            {props.isCalendarVisible && (
              <button
                className="btn btn-outline-dark mr-2 open-modal-button"
                onClick={() => props.orderActions.setOverviewView(false)}
              >
                {locale.calendar.listView}
              </button>
            )}
            {hasRole(props.authentication.auth0Roles, [
              UserRoles.TRANSPORTMANAGER,
            ]) && (
              <button
                className="btn btn-outline-dark mr-2 open-modal-button"
                onClick={() => openNewOrderModal()}
              >
                {locale.orders._newOrder}
              </button>
            )}
          </div>
        </div>
        <Spinner2 show={props.isLoading || isContractorLoading} />
        <div className="order-overview-content">
          {!props.isCalendarVisible && (
            <OrderOverviewGrid
              data={orderOverview}
              editOrder={editOrder}
              contractors={contractors}
              clasName={isFilterVisible ? 'filter-open' : ''}
              auth0Roles={props.authentication.auth0Roles}
            />
          )}
          {props.isCalendarVisible && (
            <OrderCalendar isFilterVisible={isFilterVisible} />
          )}
          <div
            className={`order-overview-filter ${isFilterVisible ? 'open' : ''}`}
          >
            <OrderOverviewFilter
              allFractions={props.allFractions}
              contractors={contractors}
              fractionOptions={fractionOptions}
              orderActions={props.orderActions}
              orderOverviewFilter={
                props.isCalendarVisible
                  ? props.orderCalendarFilter
                  : props.orderOverviewFilter
              }
              routes={routes}
              statuses={statuses}
              filterAction={
                props.isCalendarVisible
                  ? props.orderActions.setOrderCalendarFilter
                  : props.orderActions.setOrderoverviewFilter
              }
            />
          </div>
        </div>
        {(isNewOrderDialogVisible || updateDialog.show) && (
          <NewOrderDialog
            show={isNewOrderDialogVisible || updateDialog.show}
            onHide={hideNewOrderModal}
            orderToUpdate={updateDialog.order}
          />
        )}
      </div>
    </>
  );
}
